import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ReactComponent as LockImage } from "src/icons/untitled-ui/lockCircle.svg";

/**
 * AccessDeniedLockScreen Component
 * 
 * Displays a styled lock screen stating "You don't have access to this page"
 * along with a button that triggers the browser's back button history navigation.
 * 
 * Styled in accordance with the existing lock screens in MTP/Team settings.
 */
export default function AccessDeniedLockScreen({
  title = "You don't have access to this page",
  message,
  onBack,
  buttonText = "Back to Previous Page",
}) {
  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/teamDashboard";
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF00",
        minHeight: "70vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        p: 3,
        textAlign: "center",
        "& path": { stroke: "#535B66" },
      }}
    >
      <LockImage style={{ width: 140, height: 140 }} />
      <Typography
        variant="h5"
        sx={{
          color: "#535B66",
          textAlign: "center",
          pt: "15px",
          fontWeight: "600",
        }}
      >
        {title}
      </Typography>

      {message && message !== title && (
        <Typography
          variant="body2"
          sx={{
            color: "#535B6699",
            textAlign: "center",
            pt: 1,
            maxWidth: 480,
          }}
        >
          {message}
        </Typography>
      )}

      <Button
        variant="contained"
        onClick={handleBack}
        startIcon={<ArrowBackIcon />}
        sx={{
          mt: 3,
          backgroundColor: "#1a1e21",
          color: "#FFFFFF",
          borderRadius: "8px",
          padding: "8px 24px",
          textTransform: "none",
          fontSize: "14px",
          fontWeight: 600,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "rgb(88, 63, 153)",
            boxShadow: "0 6px 16px rgba(88, 63, 153, 0.35)",
            transform: "translateY(-1px)",
          },
        }}
      >
        {buttonText}
      </Button>
    </Box>
  );
}

AccessDeniedLockScreen.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  onBack: PropTypes.func,
  buttonText: PropTypes.string,
};
